import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
]
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

class RescheduleFormController extends Controller {
  static targets = ['calendar', 'selectedDate']
  static values = {
    url: String,
    calendarDataUrl: String,
    calendarData: Object,
    selectedDate: String
  }

  connect () {
    const today = new Date()
    this.viewYear = today.getFullYear()
    this.viewMonth = today.getMonth() + 1
    this.cache = {}

    if (this.hasCalendarDataValue) {
      this.cacheMonth(this.viewYear, this.viewMonth, this.calendarDataValue)
    }

    this.render()
  }

  prevMonth () {
    this.viewMonth--
    if (this.viewMonth < 1) {
      this.viewMonth = 12
      this.viewYear--
    }
    this.loadAndRender()
  }

  nextMonth () {
    this.viewMonth++
    if (this.viewMonth > 12) {
      this.viewMonth = 1
      this.viewYear++
    }
    this.loadAndRender()
  }

  async loadAndRender () {
    const key = `${this.viewYear}-${this.viewMonth}`
    if (this.cache[key]) {
      this.render()
      return
    }

    if (this.loadAbort) this.loadAbort.abort()
    this.loadAbort = new AbortController()

    try {
      const url = `${this.calendarDataUrlValue}?year=${this.viewYear}&month=${this.viewMonth}`
      const resp = await fetch(url, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.loadAbort.signal
      })
      const data = await resp.json()
      this.cacheMonth(this.viewYear, this.viewMonth, data)
    } catch (e) {
      if (e.name === 'AbortError') return
    }
    this.render()
  }

  cacheMonth (year, month, data) {
    this.cache[`${year}-${month}`] = data
  }

  getData () {
    return this.cache[`${this.viewYear}-${this.viewMonth}`] || {}
  }

  selectDay (e) {
    const dateStr = e.currentTarget.dataset.dateValue
    if (!dateStr) return

    this.selectedDateValue = dateStr

    // Update displayed selected date
    if (this.hasSelectedDateTarget) {
      const [y, m, d] = dateStr.split('-')
      this.selectedDateTarget.textContent = `${d}.${m}.${y.slice(2)}`
    }

    this.render()

    // Navigate via turbo frame
    const drawerFrame = document.querySelector('turbo-frame#drawer')
    if (drawerFrame) {
      drawerFrame.src = `${this.urlValue}?date=${encodeURIComponent(dateStr)}`
    }
  }

  formatSelectedDate () {
    if (!this.selectedDateValue) return '—'
    const [y, m, d] = this.selectedDateValue.split('-')
    return `${d}.${m}.${y.slice(2)}`
  }

  render () {
    const data = this.getData()
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

    const year = this.viewYear
    const month = this.viewMonth

    const firstDay = new Date(year, month - 1, 1)
    let startWeekday = firstDay.getDay() - 1
    if (startWeekday < 0) startWeekday = 6
    const daysInMonth = new Date(year, month, 0).getDate()

    let html = '<div class="schedule-calendar schedule-calendar--inline">'

    // Header
    html += '<div class="schedule-calendar__header">'
    html += '<button type="button" class="schedule-calendar__nav" data-action="reschedule-form#prevMonth">&lsaquo;</button>'
    html += `<span class="schedule-calendar__title">${MONTH_NAMES[month - 1]} ${year}</span>`
    html += '<button type="button" class="schedule-calendar__nav" data-action="reschedule-form#nextMonth">&rsaquo;</button>'
    html += '</div>'

    // Weekdays
    html += '<div class="schedule-calendar__weekdays">'
    WEEKDAYS.forEach(d => {
      html += `<span class="schedule-calendar__weekday">${d}</span>`
    })
    html += '</div>'

    // Days grid
    html += '<div class="schedule-calendar__days">'

    for (let i = 0; i < startWeekday; i++) {
      html += '<span class="schedule-calendar__day schedule-calendar__day--empty"></span>'
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      const entry = data[dateStr]
      const isToday = dateStr === todayStr
      const isSelected = dateStr === this.selectedDateValue
      const hasSchedule = !!entry
      const isPast = new Date(dateStr + 'T00:00:00') < today

      const classes = ['schedule-calendar__day']
      if (!hasSchedule || isPast) classes.push('schedule-calendar__day--disabled')
      if (hasSchedule && !isPast) classes.push('schedule-calendar__day--clickable')
      if (isToday) classes.push('schedule-calendar__day--today')
      if (isSelected) classes.push('schedule-calendar__day--current')
      if (hasSchedule && !isSelected) classes.push('schedule-calendar__day--has-schedule')

      let dotHtml = ''
      if (hasSchedule && !isSelected) {
        dotHtml = '<span class="schedule-calendar__dot schedule-calendar__dot--schedule-active"></span>'
      }

      if (hasSchedule && !isPast) {
        html += `<button type="button" class="${classes.join(' ')}" data-date-value="${dateStr}" data-action="reschedule-form#selectDay">${day}${dotHtml}</button>`
      } else {
        html += `<span class="${classes.join(' ')}">${day}</span>`
      }
    }

    html += '</div>'

    // Legend
    html += '<div class="schedule-calendar__legend">'
    html += '<span class="schedule-calendar__legend-item"><span class="schedule-calendar__dot schedule-calendar__dot--schedule-active" style="position:static;transform:none;display:inline-block;vertical-align:middle;margin-right:4px;"></span> Есть расписание</span>'
    html += '</div>'

    html += '</div>'

    this.calendarTarget.innerHTML = html
  }

  disconnect () {
    if (this.loadAbort) this.loadAbort.abort()
  }
}

stimulus.register('reschedule-form', RescheduleFormController)
